import { Avatar } from '#UNSAFE_Avatar';
import { styles } from './themes/RichSelectionItemStyles.css';
import { Center } from '#UNSAFE_Center';
import { View } from '#UNSAFE_View';
import { AspectRatio } from '#UNSAFE_AspectRatio';
import { TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { RichSelectionMediaItemProps } from './RichSelectionTypes';
import { classNames } from '#utils/UNSAFE_classNames';

type DisabledType = {
  isDisabled?: boolean;
};

type ThumbnailProps = TestIdProps &
  DisabledType &
  Pick<RichSelectionMediaItemProps, 'layout'> & {
    thumbnailSrc?: string;
    mediaAccessibleLabel?: string;
  };

const ThumbnailXL = ({
  thumbnailSrc,
  mediaAccessibleLabel,
  testId,
  isDisabled
}: ThumbnailProps) => {
  const testIdProps = useTestId(testId ? `${testId}_thumbnail` : undefined);

  return (
    <AspectRatio ratio="4/3">
      <img
        alt={mediaAccessibleLabel}
        className={classNames([styles.thumbnailImage, isDisabled && styles.mediaDisabledOpacity])}
        role={!mediaAccessibleLabel ? 'presentation' : undefined}
        src={thumbnailSrc}
        {...testIdProps}
      />
    </AspectRatio>
  );
};

const Thumbnail = ({
  thumbnailSrc,
  mediaAccessibleLabel,
  layout,
  testId,
  isDisabled
}: ThumbnailProps) => {
  const thumbnailClass = classNames([
    layout === 'md' ? styles.mdMediaVariant : styles.smMediaVariant,
    isDisabled && styles.mediaDisabledOpacity
  ]);
  const testIdProps = useTestId(testId ? `${testId}_thumbnail` : undefined);
  return (
    <img
      alt={mediaAccessibleLabel}
      className={thumbnailClass}
      role={!mediaAccessibleLabel ? 'presentation' : undefined}
      src={thumbnailSrc}
      {...testIdProps}
    />
  );
};

const MediumRenderer = ({
  mediaAccessibleLabel,
  thumbnailSrc,
  layout,
  avatar,
  testId,
  isDisabled
}: Partial<RichSelectionMediaItemProps> & DisabledType) => {
  const testIdProps = useTestId(testId ? `${testId}_medium` : undefined);
  let mediumMediaItem;

  if (avatar) {
    const avatarElement = (
      <Avatar {...avatar} aria-label={mediaAccessibleLabel} size="md" {...testIdProps} />
    );
    mediumMediaItem = isDisabled ? (
      <span className={styles.mediaDisabledOpacity}>{avatarElement}</span>
    ) : (
      avatarElement
    );
  } else {
    mediumMediaItem = (
      <Thumbnail
        thumbnailSrc={thumbnailSrc}
        mediaAccessibleLabel={mediaAccessibleLabel}
        layout={layout}
        testId={testId}
        isDisabled={isDisabled}
      />
    );
  }

  return <Center>{mediumMediaItem}</Center>;
};

const SmallRenderer = ({
  avatar,
  iconClass,
  isDisabled,
  layout,
  thumbnailSrc,
  mediaAccessibleLabel,
  testId
}: RichSelectionMediaItemProps & DisabledType) => {
  const testIdProps = useTestId(testId ? `${testId}_small` : undefined);
  const { iconWrapper, iconEnabledColor, iconDisabledColor, mediaDisabledOpacity } = styles;
  let smallMediaItem;

  if (avatar) {
    const avatarElement = (
      <Avatar {...avatar} aria-label={mediaAccessibleLabel} size="xs" {...testIdProps} />
    );
    smallMediaItem = isDisabled ? (
      <span className={mediaDisabledOpacity}>{avatarElement}</span>
    ) : (
      avatarElement
    );
  } else if (thumbnailSrc) {
    smallMediaItem = (
      <Thumbnail
        thumbnailSrc={thumbnailSrc}
        mediaAccessibleLabel={mediaAccessibleLabel}
        layout={layout}
        testId={testId}
        isDisabled={isDisabled}
        {...testIdProps}
      />
    );
  } else if (iconClass) {
    const classes = classNames([
      iconClass,
      iconWrapper,
      isDisabled ? iconDisabledColor : iconEnabledColor
    ]);
    smallMediaItem = <div class={classes} {...testIdProps}></div>;
  }

  return <Center>{smallMediaItem}</Center>;
};

// INFO: Media Item handling
export const RichSelectionMediaItem = ({
  avatar,
  iconClass,
  isDisabled,
  hasSecondaryText,
  mediaAccessibleLabel,
  layout,
  thumbnailSrc,
  testId
}: RichSelectionMediaItemProps & DisabledType) => {
  if (!layout) {
    return null;
  }

  switch (layout) {
    case 'xl':
      return (
        <ThumbnailXL
          thumbnailSrc={thumbnailSrc}
          mediaAccessibleLabel={mediaAccessibleLabel}
          testId={testId}
          isDisabled={isDisabled}
        />
      );

    case 'md':
      const mediumMedia = avatar ? { avatar } : thumbnailSrc ? { thumbnailSrc } : undefined;
      return (
        <View alignSelf={hasSecondaryText ? 'start' : 'center'}>
          <MediumRenderer
            mediaAccessibleLabel={mediaAccessibleLabel}
            layout={layout}
            testId={testId}
            isDisabled={isDisabled}
            {...mediumMedia}
          />
        </View>
      );

    case 'sm':
      const smallMedia = avatar
        ? { avatar, mediaAccessibleLabel }
        : thumbnailSrc
        ? { thumbnailSrc, mediaAccessibleLabel }
        : iconClass
        ? { iconClass }
        : undefined;
      return (
        <View alignSelf={hasSecondaryText ? 'start' : 'center'}>
          <SmallRenderer {...smallMedia} layout={layout} testId={testId} isDisabled={isDisabled} />
        </View>
      );

    default:
      return null;
  }
};
